import { addDiamondByElementType } from '@/constant/reward';
import { lazy, Suspense, memo } from "react";
import { useChatStore } from "@/store/useChatStore";
import { ElementType } from "@/constant/schema";
import { QuestionElementTypes } from '@/constant/element-type-group';
import { updateExampleQuestionElement } from '@/lib/elementUpdateHandler';

// 懒加载所有卡片组件
const ChatTimeline = lazy(() => import("./chat-timeline"));
const ChatTimelineSpark = lazy(() => import("./chat-timeline-spark"));
const ChatFlashcard = lazy(() => import("./chat-flashcard"));
const ChatRecommendVideos = lazy(() => import("./chat-recommend_videos"));
const ChatRecommendShorts = lazy(() => import("./chat-recommend_shorts"));
const ChatReviewVideos = lazy(() => import("./chat-review-videos"));
const ChatInteractive = lazy(() => import("./chat-interactive"));
const ChatLesson = lazy(() => import("./chat-lesson"));
const ChatSpelling = lazy(() => import("./chat-spelling"));
const ChatQuestion = lazy(() => import("./chat-question"));
const ChatDragAndDrop = lazy(() => import("./chat-drag-and-drop"));
const ChatFilling = lazy(() => import("./chat-filling"));
const ChatHighlight = lazy(() => import("./chat-highlight"));
const ChatKeyProperty = lazy(() => import("./chat-key-property"));
const ChatExampleQuestion = lazy(() => import("./chat-example-question"));
const ChatSpelling2 = lazy(() => import("./chat-spelling-2"));

// Loading fallback component
const CardLoadingFallback = () => null;

interface LearnCardProps {
  message: any;
  isTutor?: boolean;
  handleElementFinish: (element_id: string, finish_type: 'user_close' | 'auto_finish') => void;
}

const LearnCard = ({ message, handleElementFinish }: LearnCardProps) => {
  const { element_finished, is_element_finished, reviewMode } = useChatStore();

  // Debug: Log which element is being rendered
  console.log('🎨 [LearnCard] Rendering element:', {
    element_type: message?.element?.element_type,
    element_id: message?.element?.id,
    has_element: !!message?.element
  });

  // 标记元素完成，并且实际的增加钻石，
  // element_data必须包含isCorrect字段，用于判断是否回答正确
  const handleFinish = async (element_id: string, finish_type: 'user_close' | 'auto_finish', reward: boolean = false, element_data?: any) => {
    console.log(`handleFinish called: element_id=${element_id}, reward=${reward}, element_type=${message.element.element_type}, reviewMode=${reviewMode}`);

    if (reward && !is_element_finished(element_id)) {
      element_finished(element_id, element_data);

      // 在 reviewMode 下不给 diamond 奖励
      if (!reviewMode) {
        if (QuestionElementTypes.includes(message.element.element_type)) {
          // Add diamond only if user answered correctly
          if (element_data && element_data.isCorrect) {
            await addDiamondByElementType(message.element.element_type)
          }
        } else {
          // For non-question elements (like FLASH_CARD, SPELLING_DICTATION), always give diamond
          await addDiamondByElementType(message.element.element_type)
        }
      } else {
        console.log('🔄 Review mode: skipping diamond reward');
      }
    }
  };

  if (message?.element.element_type === ElementType.TIMELINE) {
    // 应该需要保留
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatTimeline
          element={message.element}
          onClose={() => {
            console.log("timeline close");
            // handleFinish(message.element.id, 'user_close')
          }}
          onFinish={() => {
            console.log("timeline finished");
            handleFinish(message.element.id, 'auto_finish', false)
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.TIMELINE_SPARK) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatTimelineSpark
          element={message.element}
          onClose={() => {
            console.log("timeline_spark close");
          }}
          onFinish={() => {
            console.log("timeline_spark finished");
            handleFinish(message.element.id, 'auto_finish', true);
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.FLASH_CARD) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatFlashcard
          element={message.element}
          onClose={() => {
            console.log("flashcard close");
          }}
          onFinish={() => {
            useChatStore.setState({ can_continue: true });
            console.log("flashcard finished");
            handleFinish(message.element.id, 'auto_finish', true);
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.RECOMMEND_VIDEOS) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatRecommendVideos
          element={message.element}
          onFinish={() => {
            console.log("recommend_videos finished");
            handleFinish(message.element.id, 'auto_finish', false);
          }}
          onClose={() => {
            console.log("recommend_videos close");
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.RECOMMEND_SHORTS) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatRecommendShorts
          element={message.element}
          onFinish={() => {
            console.log("recommend_videos finished");
            handleFinish(message.element.id, 'auto_finish', false);
          }}
          onClose={() => {
            console.log("recommend_videos close");
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.REVIEW_VIDEOS) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatReviewVideos
          element={message.element}
          onFinish={() => {
            console.log("review_videos finished");
            handleFinish(message.element.id, 'auto_finish', false);
          }}
          onClose={() => {
            console.log("review_videos close");
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.LESSON_SUMMARY) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatLesson
          element={message.element}
          onFinish={() => { }}
          onClose={() => { }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.SPELLING_DICTATION) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatSpelling
          element={message.element}
          onFinish={() => {
            handleFinish(message.element.id, 'auto_finish', true);
          }}
          onClose={() => { }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.QUESTION || message?.element.element_type === ElementType.QUESTION_V2) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatQuestion
          element={message.element}
          onFinish={(data: any) => {
            console.log("element QUESTION finished", message.element.id, data);
            // Only reward if answer is correct
            handleFinish(message.element.id, 'auto_finish', true, data);
          }}
          onClose={() => { }}
          onError={() => {
            console.log("quiz_single error");
          }}
        />
      </Suspense>
    );
  } else if (message?.element.element_type === ElementType.GRAMMAR_FILL_IN_BLANK) {
    console.log('✅ Rendering fill_in_blank with ChatFilling');
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatFilling
          element={message.element}
          onFinish={(data: any) => {
            handleFinish(message.element.id, 'auto_finish', true, data);
          }}
          onClose={() => { }}
          onError={() => { }}
        />
      </Suspense>
    );
  } else if (message?.element.element_type === ElementType.GRAMMAR_DRAG_AND_DROP) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatDragAndDrop
          element={message.element}
          onFinish={(data: any) => {
            handleFinish(message.element.id, 'auto_finish', true, data);
          }}
          onClose={() => { }}
          onError={() => { }}
        />
      </Suspense>
    );
  } else if (message?.element.element_type === ElementType.HIGHLIGHT) {
    return (
      <div className="w-full">
        <Suspense fallback={<CardLoadingFallback />}>
          <ChatHighlight
            element={message.element}
            onFinish={(data: any) => {
              handleFinish(message.element.id, 'auto_finish', true, data);
            }}
            onClose={() => { }}
          />
        </Suspense>
      </div>
    );
  } else if (message?.element.element_type === ElementType.INTERACTIVE) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatInteractive
          element={message.element}
          onFinish={() => {
            handleFinish(message.element.id, 'auto_finish', false);
          }}
          onClose={() => { }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.KEY_PROPERTY) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatKeyProperty 
          element={message.element} 
          onFinish={(data: any) => {
            // 记录进度，并直接展示下一个卡片
            handleElementFinish(message.element.id, 'auto_finish');
            element_finished(message.element.id, data);
            console.log("key_property finished", message.element.id, data);
          }} 
          onClose={(data: any) => {
            // 记录进度，让continue按钮可点击
            console.log("记录进度，让continue按钮可点击", message.element.id, data);
            const currentElement = useChatStore.getState().current_element?.id;
            element_finished(message.element.id, data);
            if (currentElement === message.element.id) {
              setTimeout(() => {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: 'smooth'
                });
              }, 100);
              useChatStore.setState({ can_continue: true });
            }
          }}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.EXAMPLE_QUESTION) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatExampleQuestion
          element={message.element}
          onFinish={(data: any) => {
            // 跳过
            handleElementFinish(message?.element?.id, 'auto_finish');
            element_finished(message.element.id, data);
          }}
          onClose={(data: any) => {
            // 回答了题目
            const currentElement = useChatStore.getState().current_element?.id;
            // element_finished(message.element.id, data);
            handleFinish(message.element.id, 'auto_finish', true, data);
            if (currentElement === message.element.id) {
              setTimeout(() => {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: 'smooth'
                });
              }, 100);
              useChatStore.setState({ can_continue: true });
            }
          }}
          onUpdate={updateExampleQuestionElement}
        />
      </Suspense>
    )
  } else if (message?.element.element_type === ElementType.SPELLING_DICTATION_V2) {
    return (
      <Suspense fallback={<CardLoadingFallback />}>
        <ChatSpelling2
          element={message.element}
          onFinish={() => {
            handleFinish(message.element.id, 'auto_finish', true);
          }}
          onClose={() => { }}
        />
      </Suspense>
    )
  } else {
    return (
      <div>Unsupported element type: {message.element.element_type}, id: {message.element.id}</div>
    )
  }
};

// 自定义比较函数：只在关键 props 变化时才重新渲染
const arePropsEqual = (prevProps: LearnCardProps, nextProps: LearnCardProps) => {
  // 比较 element id - 如果 id 不同，则是不同的元素
  if (prevProps.message?.element?.id !== nextProps.message?.element?.id) {
    return false;
  }

  // 比较 element type - 如果类型不同，需要重新渲染
  if (prevProps.message?.element?.element_type !== nextProps.message?.element?.element_type) {
    return false;
  }

  // 比较 handleElementFinish 函数引用（父组件应该用 useCallback 包装）
  if (prevProps.handleElementFinish !== nextProps.handleElementFinish) {
    return false;
  }

  // 其他属性都相同，不需要重新渲染
  return true;
};

// 使用 React.memo 包装组件，传入自定义比较函数
export default memo(LearnCard, arePropsEqual);  
